import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaChevronDown,
  FaEnvelope,
  FaBars,
  FaTimes,
} from "react-icons/fa";
import { Link } from "react-router-dom";

/* ---------------- Top Navigation ---------------- */

const TopNav = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(8, 10, 16, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const NavInner = styled.div`
  max-width: 1100px;
  margin: auto;
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;


const Brand = styled(Link)`
  font-size: 18px;
  font-weight: 700;
  color: #ffb97b;
  text-decoration: none;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 22px;

  a {
    color: #ddd;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      color: #ffb97b;
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Burger = styled.div`
  display: none;
  font-size: 22px;
  color: #fff;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: #080a10;
  z-index: 200;
  padding: 90px 30px;
`;

const Close = styled.div`
  position: absolute;
  top: 22px;
  right: 22px;
  font-size: 24px;
  color: #fff;
  cursor: pointer;
`;

const MobileLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;

  a {
    font-size: 20px;
    font-weight: 600;
    color: #fff;
    text-decoration: none;

    &:hover {
      color: #ffb97b;
    }
  }
`;


/* ---------------- Page Styles ---------------- */

const Page = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top, #141823, #080a10);
  color: #ffffff;
  padding: 140px 20px 90px;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: auto;
`;

const Title = styled.h1`
  font-size: 42px;
  text-align: center;
  margin-bottom: 12px;
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  opacity: 0.85;
  margin-bottom: 70px;
  color: #d6d6d6;
`;

/* -------- Timeline -------- */

const Timeline = styled.div`
  position: relative;
  margin-left: 25px;
  border-left: 2px solid rgba(255, 255, 255, 0.15);

  @media (max-width: 600px) {
    margin-left: 10px;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 50px;
  padding-left: 40px;

  @media (max-width: 600px) {
    padding-left: 30px;
  }
`;

const Dot = styled.div`
  position: absolute;
  left: -11px;
  top: 6px;
  width: 18px;
  height: 18px;
  background: #ffb97b;
  border-radius: 50%;
  box-shadow: 0 0 20px rgba(255, 185, 123, 0.6);
`;

const TimelineTitle = styled.h3`
  font-size: 22px;
  margin-bottom: 12px;
  color: #ffcc9c;
`;

/* -------- Accordion -------- */

const Accordion = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  overflow: hidden;
`;

const AccordionHeader = styled.div`
  padding: 18px 22px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.03);
`;

const AccordionContent = styled(motion.div)`
  padding: 0 22px 22px;
  font-size: 15px;
  line-height: 1.7;
  opacity: 0.95;
`;

const Icon = styled(motion.span)`
  display: flex;
  align-items: center;
`;

const Highlight = styled.span`
  color: #ffb97b;
  font-weight: 600;
`;

/* ---------------- Accordion Item ---------------- */

const AccordionItem = ({ title, children }) => {
  return (
    <>
      <AccordionHeader>
        {title}
        <Icon animate={{ rotate: 180 }}>
          <FaChevronDown />
        </Icon>
      </AccordionHeader>

      <AccordionContent
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: "auto", opacity: 1 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
      >
        {children}
      </AccordionContent>
    </>
  );
};

/* ---------------- Page ---------------- */

const RefundPolicy = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    // Force scroll to top on page load
    window.scrollTo(0, 0);

    // Extra safety for mobile browsers
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <>
      {/* Top Navigation */}
      <TopNav>
        <NavInner>
          <Brand to="/">RechargeJunction</Brand>

          <NavLinks>
            <Link to="/">Home</Link>
            <Link to="/login">Login</Link>
            <Link to="/register">Register</Link>
            <Link to="/contact">Contact</Link>
          </NavLinks>

          <Burger onClick={() => setMenuOpen(true)}>
            <FaBars />
          </Burger>
        </NavInner>
      </TopNav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <MobileMenu
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3 }}
          >
            <Burger onClick={() => setMenuOpen(false)}>
              <FaTimes />
            </Burger>

            <MobileLinks>
              <Link to="/" onClick={() => setMenuOpen(false)}>Home</Link>
              <Link to="/login" onClick={() => setMenuOpen(false)}>Login</Link>
              <Link to="/register" onClick={() => setMenuOpen(false)}>Register</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)}>Contact</Link>
            </MobileLinks>
          </MobileMenu>
        )}
      </AnimatePresence>

      {/* Page Content */}
      <Page>
        <Container>
          <Title>Refund & Cancellation Policy</Title>
          <Subtitle>Transparent, fair, and customer-first policies</Subtitle>

          <Timeline>
            {/* STEP 1 */}
            <TimelineItem>
              <Dot />
              <TimelineTitle>Customer Satisfaction Commitment</TimelineTitle>
              <Accordion>
                <AccordionItem title="Our Refund Philosophy">
                  Our focus is complete customer satisfaction. If you are
                  displeased with the services provided, we will refund the
                  money provided the reasons are genuine and verified after
                  investigation.
                </AccordionItem>
                <AccordionItem title="Before You Purchase">
                  Please read the fine prints of each deal carefully, as it
                  contains complete details about the services or products you
                  purchase.
                </AccordionItem>
              </Accordion>
            </TimelineItem>

            {/* STEP 2 */}
            <TimelineItem>
              <Dot />
              <TimelineTitle>Cancellation Policy</TimelineTitle>
              <Accordion>
                <AccordionItem title="How to Cancel">
                  For cancellations, please contact us at{" "}
                  <Highlight>help.rechargejunction@gmail.com</Highlight>.
                </AccordionItem>
                <AccordionItem title="Cancellation Timeline">
                  Requests received later than{" "}
                  <Highlight>3 business days</Highlight> prior to the end of the
                  current service period will be treated as cancellation for the
                  next service period.
                </AccordionItem>
              </Accordion>
            </TimelineItem>

            {/* STEP 3 */}
            <TimelineItem>
              <Dot />
              <TimelineTitle>Refund Process</TimelineTitle>
              <Accordion>
                <AccordionItem title="Eligibility for Refund">
                  We strive to deliver the best service. If a client is not
                  completely satisfied, a refund may be issued after internal
                  review.
                </AccordionItem>
                <AccordionItem title="Refund Method">
                  Credit card refunds will be issued to the original card used
                  during purchase. Payment gateway refunds will be processed to
                  the same account.
                </AccordionItem>
              </Accordion>
            </TimelineItem>

            {/* STEP 4 */}
            <TimelineItem>
              <Dot />
              <TimelineTitle>Need Assistance?</TimelineTitle>
              <Accordion>
                <AccordionItem title="Contact Support">
                  <FaEnvelope /> &nbsp; Reach us anytime at{" "}
                  <Highlight>help.rechargejunction@gmail.com</Highlight> for
                  refund or cancellation queries.
                </AccordionItem>
              </Accordion>
            </TimelineItem>
          </Timeline>
        </Container>
      </Page>
    </>
  );
};

export default RefundPolicy;